import { API, graphqlOperation } from "aws-amplify";
import { GraphQLResult } from "@aws-amplify/api";
import { listLLMProjects } from "../graphql/queries";
import { ListLLMProjectsQuery, LLMProject } from "../API";

/**
 * Fetch all projects for admin use (without pagination limits)
 * Returns basic project information for cost analysis
 */
export async function fetchAllLLMProjects(options: { activeOnly?: boolean } = {}): Promise<LLMProject[]> {
  const allProjects: LLMProject[] = [];
  let nextToken: string | null = null;
  let hasMorePages = true;
  
  while (hasMorePages) {
    try {
      const result: GraphQLResult<ListLLMProjectsQuery> = await API.graphql(
        graphqlOperation(listLLMProjects, {
          limit: 100, // Use reasonable batch size
          nextToken: nextToken,
          ...(options.activeOnly === false
            ? {}
            : {
                filter: {
                  status: {
                    eq: "active"
                  }
                }
              })
        })
      ) as GraphQLResult<ListLLMProjectsQuery>;
      
      if (result.data?.listLLMProjects?.items) {
        const items = result.data.listLLMProjects.items.filter(
          (item: any): item is LLMProject => item !== null
        );
        allProjects.push(...items);
      }
      
      nextToken = result.data?.listLLMProjects?.nextToken || null;
      hasMorePages = !!nextToken;
      
      // Safety break to prevent infinite loops
      if (allProjects.length > 10000) {
        console.warn('Fetched >10k projects, stopping for performance');
        break;
      }
      
    } catch (error) {
      console.error('Error fetching projects:', error);
      throw new Error(`Failed to fetch projects: ${error}`);
    }
  }
  
  console.log(`Fetched ${allProjects.length} projects`);
  return allProjects;
}

/**
 * Fetch basic project information by ID
 */
export async function fetchLLMProjectBasic(projectId: string): Promise<LLMProject | null> {
  try {
    const result: GraphQLResult<any> = await API.graphql(
      graphqlOperation(`
        query GetLLMProjectBasic($id: ID!) {
          getLLMProject(id: $id) {
            id
            name
            status
            description
            createdAt
          }
        }
      `, {
        id: projectId
      })
    );
    
    return result.data?.getLLMProject || null;
  } catch (error) {
    console.error(`Error fetching project ${projectId}:`, error);
    return null;
  }
}
